import {
  MEETING_PASSWORD_ALPHABET,
  MEETING_PASSWORD_LENGTH,
} from '../constants/meetings.constants'

/**
 * Password generation + SHA-256 hashing helpers.
 *
 * Hashing is done client-side via the WebCrypto SubtleCrypto API so we never
 * store plaintext in the credentials slot. A per-meeting salt is generated
 * alongside the hash; both go into the credential record.
 *
 * Production note: real backends should re-hash with a slow KDF (argon2 /
 * bcrypt) on the server. SHA-256 here is sufficient for a mock to avoid
 * shipping plaintext-only storage.
 */

const SUBTLE = typeof crypto !== 'undefined' ? crypto.subtle : undefined

const toHex = (buf: ArrayBuffer): string =>
  Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')

export function generatePassword(): string {
  const alphabet = MEETING_PASSWORD_ALPHABET
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const bytes = new Uint32Array(MEETING_PASSWORD_LENGTH)
    crypto.getRandomValues(bytes)
    let out = ''
    for (let i = 0; i < MEETING_PASSWORD_LENGTH; i++) {
      out += alphabet[bytes[i] % alphabet.length]
    }
    return out
  }
  let out = ''
  for (let i = 0; i < MEETING_PASSWORD_LENGTH; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)]
  }
  return out
}

export function generateSalt(byteLength = 16): string {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const buf = new Uint8Array(byteLength)
    crypto.getRandomValues(buf)
    return toHex(buf.buffer)
  }
  return Math.random().toString(16).slice(2).padEnd(byteLength * 2, '0')
}

async function sha256Hex(value: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(value)
  if (SUBTLE) {
    const digest = await SUBTLE.digest('SHA-256', data)
    return toHex(digest)
  }
  // Fallback: very weak djb2 hash. Only reachable in old browsers without
  // SubtleCrypto — production should never hit this.
  let hash = 5381
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 33) ^ value.charCodeAt(i)
  }
  return `fallback_${(hash >>> 0).toString(16)}`
}

export async function hashPassword(
  password: string,
  salt: string,
): Promise<string> {
  return sha256Hex(`${salt}:${password}`)
}

export async function verifyPassword(
  password: string,
  salt: string,
  expectedHash: string,
): Promise<boolean> {
  const actual = await hashPassword(password, salt)
  // Constant-time string compare (length-checked) — not cryptographically
  // perfect in JS but avoids early-exit timing leaks on short strings.
  if (actual.length !== expectedHash.length) return false
  let mismatch = 0
  for (let i = 0; i < actual.length; i++) {
    mismatch |= actual.charCodeAt(i) ^ expectedHash.charCodeAt(i)
  }
  return mismatch === 0
}
